// Small custom controls for the Mobile Apps page (no native select/switch — UI rules).
import React from 'react';

export const RUBY = '#DC143C';

export function Switch({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      onClick={() => onChange(!on)}
      className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 min-h-[44px] min-w-[48px]"
    >
      <span className="absolute inset-x-0 top-1/2 h-7 -translate-y-1/2 rounded-full transition" style={{ background: on ? RUBY : '#d6d3d1' }} />
      <span className="relative ml-1 h-5 w-5 rounded-full bg-white shadow transition" style={{ transform: on ? 'translateX(20px)' : 'none' }} />
    </button>
  );
}

export function Seg<T extends string>({ value, options, onChange, dark }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; dark?: boolean }) {
  return (
    <div className={`inline-flex rounded-full p-1 ${dark ? 'bg-white/10' : 'bg-stone-200'}`}>
      {options.map((o) => (
        <button key={o.id} type="button" onClick={() => onChange(o.id)}
          className={`min-h-[40px] rounded-full px-4 text-sm font-medium transition ${value === o.id
            ? (dark ? 'bg-white text-stone-900' : 'bg-white text-stone-900 shadow-sm')
            : (dark ? 'text-stone-300 hover:text-white' : 'text-stone-600 hover:text-stone-900')}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Card({ title, sub, right, children }: { title?: string; sub?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-white">
      {(title || right) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 px-5 py-4">
          <div>{title && <h2 className="text-[15px] font-semibold text-stone-900">{title}</h2>}{sub && <p className="text-xs text-stone-500">{sub}</p>}</div>
          {right}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

const TONES = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200', warn: 'bg-amber-50 text-amber-700 border-amber-200',
  bad: 'bg-red-50 text-red-700 border-red-200', info: 'bg-sky-50 text-sky-700 border-sky-200',
  ruby: 'bg-rose-50 text-rose-800 border-rose-200', mute: 'bg-stone-100 text-stone-500 border-stone-200',
} as const;
export function Chip({ tone = 'mute', children }: { tone?: keyof typeof TONES; children: React.ReactNode }) {
  return <span className={`inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium ${TONES[tone]}`}>{children}</span>;
}

export const PLATFORMS = [{ id: 'ios' as const, label: 'iPhone' }, { id: 'android' as const, label: 'Android' }];
export const APPS = [{ id: 'rubytill' as const, label: 'RubyTill' }, { id: 'rubytill-admin' as const, label: 'RubyTill Admin' }];

export function Btn({ children, onClick, primary, disabled, small }: { children: React.ReactNode; onClick?: () => void; primary?: boolean; disabled?: boolean; small?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-xl border font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${small ? 'min-h-[40px] px-3 text-xs' : 'min-h-[44px] px-4 text-sm'} ${primary ? 'border-[#DC143C] bg-[#DC143C] text-white hover:bg-[#9F1239]' : 'border-stone-200 bg-white text-stone-800 hover:border-stone-400'}`}>
      {children}
    </button>
  );
}
